<template>
    <div class="container webhooks-form">
        <v-title :lineage="[{'Webhooks':'webhooks'}]">
            {{isFormUpdating ? 'Update' : 'Add'}} Webhook
            <div slot="actions">
                <router-link
                    v-if="$auth.$pages['webhooks'].isAllowed"
                    class="btn inline-s" :to="{name: 'webhooks'}">
                    Cancel
                </router-link>
                <r-button
                    type="primary"
                    @click="save"
                    :loading="isLoading">Save Webhook</r-button>
            </div>
        </v-title>
        <v-inline-notifications
            v-if="repeatMessage"
            type="positive"
            title="Webhook successfully created"
            class="stack-l">
            <span slot="content">
                Would you like to add <r-button type="link" @click="newFromContinue">another webhook</r-button>
                <template v-if="$auth.$pages['webhooks'].isAllowed"> or go back to the <router-link :to="{name: 'webhooks'}">webhooks list</router-link></template>?
            </span>
        </v-inline-notifications>
        <v-error-messages
            :has-frontend-errors="$v.webhook.$error"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.webhook.method.required">Webhook method is required.</li>
            <li class="list-item" v-if="!$v.webhook.id.resourceId"><span>The webhook ID cannot contain spaces or special characters other than <strong>@ ~ _ -</strong> and should be less than <strong>50</strong> characters long.</span></li>
            <li class="list-item" v-if="!$v.webhook.url.required">Webhook URL is required.</li>
            <li class="list-item" v-if="!$v.webhook.url.url">Webhook URL is invalid.</li>
            <li class="list-item" v-if="!$v.webhook.eventsFilter.required">A event filter is required.</li>
            <li class="list-item" v-if="$v.webhook.headers.$error">Webhook header names and values are required.</li>
        </v-error-messages>
        <v-error-messages :has-frontend-errors="$v.webhookCredential.$error" class="stack-l">
            <li class="list-item" v-if="!webhookCredential.auth.isNoAuth && !$v.webhookCredential.auth.type.required">Authorization Type is invalid.</li>
            <template v-if="webhookCredential.auth.isBasicAuth || webhookCredential.auth.isDigestAuth">
                <li class="list-item" v-if="!$v.webhookCredential.auth.username.required">Authorization username is
                    required.
                </li>
                <li class="list-item" v-if="!$v.webhookCredential.auth.password.required">Authorization password is
                    required.
                </li>
            </template>
            <template v-if="webhookCredential.auth.isOAuth1">
                <li class="list-item" v-if="!$v.webhookCredential.auth.consumerKey.required">Consumer Key is required.
                </li>
                <li class="list-item" v-if="!$v.webhookCredential.auth.consumerSecret.required">Consumer Secret is
                    required.
                </li>
                <li class="list-item" v-if="!$v.webhookCredential.auth.token.required">Token is required.</li>
                <li class="list-item" v-if="!$v.webhookCredential.auth.tokenSecret.required">Token Secret is required.
                </li>
            </template>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">ID</h2>
            <p slot="description" class="text-muted">Enter a webhook ID to identify your new webhook. It is a good idea
                to use a name that is easy to read.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                auto-focus
                                v-model="webhook.id"
                                :validate="$v.webhook.id"
                                label="Webhook ID"
                                :disabled="isFormUpdating"
                                help-text="ID used to uniquely identify this webhook. If not provided, it will be generated automatically."
                            />
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Status</h2>
            <p slot="description" class="text-muted">Specify the active or inactive status of your webhook.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-full">
                            <div class="grid-fitted grid-unstackable cursor-pointer">
                                <div class="grid-item" @click="toggleWebhookIsActive">Webhook {{webhook.isActive ?
                                    'active':'inactive'}}
                                </div>
                                <div class="grid-item no-flex">
                                    <v-toggle
                                        :value="true"
                                        :selected="webhook.isActive"
                                        @input="toggleWebhookIsActive"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Filter Events</h2>
            <p slot="description" class="text-muted">You can subscribe to all or specific events.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-full">
                            <v-radio
                                class="stack-m"
                                group="eventsFilter"
                                label="Send all event types"
                                :value="false"
                                :model="isSelectingFilters"
                                @update="updateSelectionState"
                            />
                            <v-radio
                                group="eventsFilter"
                                label="Send specific event types"
                                :value="true"
                                :model="isSelectingFilters"
                                @update="updateSelectionState"
                            />
                        </div>
                        <div v-if="isSelectingFilters" class="grid-item grid-item-full">
                            <v-select
                                label="Events Filter"
                                :options="eventsFilterTypes"
                                :multiple="true"
                                :allow-empty="true"
                                :hide-selected="true"
                                :close-on-select="false"
                                placeholder="Type to search or select"
                                v-model="webhook.eventsFilter"
                                :validate="$v.webhook.eventsFilter"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Endpoint</h2>
            <p slot="description" class="text-muted">Configure the HTTP method, URL, authentication and headers for your
                webhook endpoint.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-full">
                            <div class="field">
                                <label class="field-label">Webhook URL</label>
                                <div class="field-group">
                                    <v-select
                                        class="http-verbs no-flex"
                                        :options="httpVerbsList"
                                        options-label="label"
                                        options-key="key"
                                        placeholder="Select HTTP Verb"
                                        v-model="webhook.method"
                                        :validate="$v.webhook.method"
                                    />
                                    <r-input
                                        class="input-url"
                                        v-model="webhook.url"
                                        :validate="$v.webhook.url"
                                        :disabled="hasCredentialHash"
                                        placeholder="Enter the webhook URL"/>
                                </div>
                            </div>
                            <span class="field-caption">The webhook URL must include http or https. e.g. https://www.mywebhook.com/endpoint</span>
                        </div>
                    </div>
                </div>
                <div class="tile-content">
                    <r-tabs :divided="true" content-class="no-bottom-inset" menu-class="no-margin-bottom">
                        <r-tab name="Authorization" :active="true">
                            <div class="grid">
                                <div v-if="isFormUpdating" class="grid-item grid-item-full">
                                    <r-checkbox :label="credentialHashLabel"
                                                :selected="hasCredentialHash"
                                                :value="true"
                                                @input="toggleCredentialHashChecked"/>
                                </div>
                                <div v-if="!hasCredentialHash" class="grid-item grid-item-half grid-item-break-half">
                                    <div class="field" :class="{'is-error': $v.webhookCredential.auth.type.$error}">
                                        <div class="field-control has-icon-right">
                                            <v-select
                                                label="Authorization Type"
                                                :options="authorizationTypes"
                                                option-label="value"
                                                option-key="key"
                                                placeholder="Type to search or select"
                                                v-model="webhookCredential.auth.type"
                                                @input="resetAuth"
                                                :validate="$v.webhookCredential.auth.type"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <template v-if="!hasCredentialHash">
                                    <template
                                        v-if="webhookCredential.auth.isDigestAuth || webhookCredential.auth.isBasicAuth">
                                        <div class="grid-item grid-item-half grid-item-break-half">
                                            <r-input v-model="webhookCredential.auth.username"
                                                     :validate="$v.webhookCredential.auth.username"
                                                     label="Username"/>
                                        </div>
                                        <div class="grid-item grid-item-half grid-item-break-half">
                                            <r-input v-model="webhookCredential.auth.password"
                                                     :validate="$v.webhookCredential.auth.password" :password="true"
                                                     label="Password"/>
                                        </div>
                                    </template>
                                    <template v-else-if="webhookCredential.auth.isOAuth1">
                                        <div class="grid-item grid-item-half grid-item-break-half">
                                            <r-input v-model="webhookCredential.auth.consumerKey"
                                                     :validate="$v.webhookCredential.auth.consumerKey"
                                                     label="Consumer Key"/>
                                        </div>
                                        <div class="grid-item grid-item-half grid-item-break-half">
                                            <r-input v-model="webhookCredential.auth.consumerSecret"
                                                     :validate="$v.webhookCredential.auth.consumerSecret"
                                                     label="Consumer Secret"/>
                                        </div>
                                        <div class="grid-item grid-item-half grid-item-break-half">
                                            <r-input v-model="webhookCredential.auth.token"
                                                     :validate="$v.webhookCredential.auth.token" label="Token"/>
                                        </div>
                                        <div class="grid-item grid-item-half grid-item-break-half">
                                            <r-input v-model="webhookCredential.auth.tokenSecret"
                                                     :validate="$v.webhookCredential.auth.tokenSecret"
                                                     label="Token Secret"/>
                                        </div>
                                    </template>
                                </template>
                            </div>
                        </r-tab>
                        <r-tab :name="headersTabLabel">
                            <ul class="list list-group list-divided">
                                <li class="list-item align-items-center" v-for="(header, index) in webhook.headers"
                                    v-bind:key="index">
                                    <v-toggle
                                        class="inline-m no-flex"
                                        :value="true"
                                        :selected="header.isActive"
                                        @input="toggleHeadersIsActive(index)"
                                    />
                                    <div class="field list-item-content inline-m">
                                        <r-input
                                            v-model="header.name"
                                            :validate="$v.webhook.headers.$each[index].name"
                                            placeholder="Name"/>
                                    </div>
                                    <div class="field list-item-content inline-m">
                                        <r-input
                                            v-model="header.value"
                                            :validate="$v.webhook.headers.$each[index].value"
                                            placeholder="Value"/>
                                    </div>
                                    <span class="list-item-content no-flex">
                                        <v-icon-button type="basic" @click="deleteHeader(index)"><v-icon icon="trash"/></v-icon-button>
                                    </span>
                                </li>
                                <r-button type="link" :class="{'add-header': webhook.headers.length > 0}"
                                          @click="addHeader">Add header
                                </r-button>
                            </ul>
                        </r-tab>
                    </r-tabs>
                </div>
                <div class="tile-content">
                    <r-button type="default" :disabled="isLoading" @click="test">Test Webhook</r-button>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <router-link
                    v-if="$auth.$pages['webhooks'].isAllowed"
                    class="btn inline-s" :to="{name: 'webhooks'}">
                    Cancel
                </router-link>
                <r-button
                    type="primary"
                    @click="save"
                    :loading="isLoading">Save Webhook</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import GlobalWebhookModel from '@/common/models/global-webhook-model';
    import vTitle from '@/components/v-title';
    import {
        vErrorMessages,
        vFormTile,
        vIcon,
        vIconButton,
        vRadio,
        vSelect,
        vToggle,
    } from '@/components/form';
    import {required, requiredIf, url} from 'vuelidate/lib/validators';
    import resourceId from '@/components/vuelidate/validators/resource-id';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import WebhookCredentialModel from '@/common/models/webhook-credential-model';
    import {AuthorizationTypesList} from '@/components/webhook/authorization-types';
    import {HttpVerbsList} from '@/components/webhook/http-verbs';
    import _ from '@/common/helpers';

    export default {
        name: 'webhook-form',
        mixins: [FormLoaderMixin],
        components: {
            vTitle,
            vIcon,
            vIconButton,
            vErrorMessages,
            vInlineNotifications,
            vFormTile,
            vRadio,
            vSelect,
            vToggle,
        },
        props: {
            id: {
                type: [String, Number],
                default: null,
            },
            continue: {
                type: String,
                default: null,
            },
        },
        computed: {
            repeatMessage() {
                return this.continue !== null;
            },
            credentialHashLabel() {
                return `Currently using credentials with hash: ${this.webhook.credentialHash}`;
            },
            headersTabLabel() {
                const slug = this.webhook.headers.length
                    ? ` (${this.webhook.headers.length})`
                    : '';
                return `Headers${slug}`;
            },
            hasAllEventsFilterSelected() {
                // consider no filters being selected as having
                // all of them enabled at once
                return this.webhook.eventsFilter.length === 0;
            },
        },
        data() {
            return {
                authorizationTypes: [...AuthorizationTypesList],
                isSelectingFilters: false,
                hasCredentialHash: false,
                selectedEventsFilter: [],
                webhook: new GlobalWebhookModel(),
                webhookCredential: new WebhookCredentialModel(),
                eventsFilterTypes: Object.keys(GlobalWebhookModel.EventFilters).map(key => ({
                    label: _.unCamelCase(key),
                    value: GlobalWebhookModel.EventFilters[key],
                })),
                httpVerbsList: [...HttpVerbsList],
            };
        },
        validations() {
            const validation = {
                webhook: {
                    id: {resourceId},
                    method: {required},
                    url: {
                        required,
                        url,
                    },
                    eventsFilter: {
                        required: requiredIf(() => !this.hasAllEventsFilterSelected),
                    },
                    headers: {
                        $each: {
                            name: {
                                required,
                            },
                            value: {
                                required,
                            },
                        },
                    },
                },
                webhookCredential: {
                    auth: {
                        type: {
                            required,
                        },
                    },
                },
            };
            if (this.hasCredentialHash) {
                validation.webhookCredential = {};
            } else {
                if (this.webhookCredential.auth.isBasicAuth
                    || this.webhookCredential.auth.isDigestAuth) {
                    validation.webhookCredential.auth = {
                        ...validation.webhookCredential.auth,
                        username: {required},
                        password: {required},
                    };
                }
                if (this.webhookCredential.auth.isOAuth1) {
                    validation.webhookCredential.auth = {
                        ...validation.webhookCredential.auth,
                        consumerKey: {required},
                        consumerSecret: {required},
                        token: {required},
                        tokenSecret: {required},
                    };
                }
            }
            return validation;
        },
        methods: {
            newFromContinue() {
                this.$v.$reset();
                this.webhook = new GlobalWebhookModel();
                this.$router.push({name: 'add-webhook'});
            },
            addHeader() {
                const header = {
                    isActive: true,
                    name: '',
                    value: '',
                };
                this.webhook.headers.push(header);
            },
            deleteHeader(index) {
                this.webhook.headers.splice(index, 1);
            },
            async fetchData() {
                if (this.isFormUpdating) {
                    try {
                        const webhook = await this.$api(({webhooks}) => webhooks.get({id: this.id}));
                        this.webhook = new GlobalWebhookModel(webhook.fields);
                        if (this.webhook.credentialHash) {
                            this.hasCredentialHash = true;
                        }
                        // set selection flag on update
                        this.isSelectingFilters = !this.hasAllEventsFilterSelected;
                    } catch (error) {
                        this.$toast.fromError(error, `The webhook you are looking for could not be found.`);
                        this.$router.push({name: 'webhooks'});
                    }
                }
            },
            async test() {
                this.$v.$touch();
                if (!this.$v.webhook.$error && !this.$v.webhookCredential.$error) {
                    this.isLoading = true;
                    try {
                        const testWebhook = new GlobalWebhookModel(this.webhook.getPayload());
                        const paramsWebhookCredential = {
                            data: this.webhookCredential.getPayload(),
                        };
                        const {fields} = await this.$api(({credentialHashes}) => credentialHashes.createWebhookCredential(paramsWebhookCredential));
                        testWebhook.credentialHash = fields.hash;
                        await this.$api(({previews}) => previews.webhook({data: testWebhook.getPayload()}));
                        this.$toast.positive(`Webhook successfully tested`);
                    } catch (error) {
                        this.serverError = error;
                        this.$toast.negative('Test failed, please confirm your webhook credentials and try again.');
                    } finally {
                        this.isLoading = false;
                    }
                }
            },
            toggleCredentialHashChecked() {
                this.hasCredentialHash = !this.hasCredentialHash;
            },
            toggleWebhookIsActive() {
                this.webhook.isActive = !this.webhook.isActive;
            },
            toggleHeadersIsActive(index) {
                this.webhook.headers[index].isActive = !this.webhook.headers[index].isActive;
            },
            async save() {
                this.$v.$touch();
                if (!this.$v.webhook.$error && !this.$v.webhookCredential.$error) {
                    this.isLoading = true;
                    try {
                        const paramsWebhookCredential = {
                            data: this.webhookCredential.getPayload(),
                        };
                        const updatedWebhook = new GlobalWebhookModel(this.webhook.getPayload());
                        if (!this.hasCredentialHash) {
                            const {fields} = await this.$api(({credentialHashes}) => credentialHashes.createWebhookCredential(paramsWebhookCredential));
                            updatedWebhook.credentialHash = fields.hash;
                        }
                        const id = this.webhook.id ? this.webhook.id.trim() : '';
                        const paramsWebhook = {
                            id,
                            data: updatedWebhook.getPayload(),
                        };
                        // if we're not selecting filters, reset the array
                        if (!this.isSelectingFilters) {
                            paramsWebhook.data.eventsFilter = [];
                        }
                        if (this.isFormUpdating) {
                            paramsWebhook.id = this.webhook.id;
                        }
                        const method = this.isFormUpdating ? 'update' : 'create';
                        const webhook = await this.$api(({webhooks}) => webhooks[method](paramsWebhook));
                        if (!this.continue && !this.isFormUpdating) {
                            window.scrollTo(0, 0);
                            this.$router.push({
                                name: 'update-webhook',
                                params: {id: webhook.fields.id, continue: 'continue'},
                            });
                            this.webhook = new GlobalWebhookModel(webhook.fields);
                        } else {
                            this.$toast.positive('Webhook successfully updated');
                            this.$router.push({name: 'update-webhook', params: {id: webhook.fields.id}});
                        }
                        this.isLoading = false;
                    } catch (error) {
                        this.serverError = error;
                        this.isLoading = false;
                        this.$toast.fromError(error);
                    }
                }
            },
            resetAuth() {
                this.webhookCredential.auth.username = '';
                this.webhookCredential.auth.password = '';
                this.webhookCredential.auth.consumerKey = '';
                this.webhookCredential.auth.consumerSecret = '';
                this.webhookCredential.auth.token = '';
                this.webhookCredential.auth.tokenSecret = '';
            },
            updateSelectionState(state) {
                this.isSelectingFilters = state;
            },
        },
        watch: {
            'webhook.url': function watchWebhookUrl() {
                const parser = document.createElement('a');
                parser.href = this.webhook.url.toLowerCase();
                this.webhookCredential.host = parser.hostname;
            },
        },
    };
</script>
